import orbitguardPng from '@public/img/icons/orbitguard.png';
import './orbit-guard-menu.css';
import { MenuMode, ToastMsgType } from '@app/engine/core/interfaces';
import { EventBusEvent } from '@app/engine/events/event-bus-events';
import { KeepTrackPlugin } from '@app/engine/plugins/base-plugin';
import { html } from '@app/engine/utils/development/formatter';
import { errorManagerInstance } from '@app/engine/utils/errorManager';
import { getEl } from '@app/engine/utils/get-el';
import { showLoading } from '@app/engine/utils/showLoading';
import { keepTrackApi } from '../../keepTrackApi';
import { SelectSatManager } from '../select-sat-manager/select-sat-manager';
export class OrbitGuardMenuPlugin extends KeepTrackPlugin {
    id = 'OrbitGuardMenuPlugin';
    dependencies_ = [];
    maneuverDataSrc = 'http://192.34.81.138:8501/orbitguard'; // API endpoint
    bearerToken = 'Bearer guruspace5172x2';
    selectSatIdOnCruncher_ = null;
    orbitGuardEvents = [];
    // Pagination variables
    pageSize = 20; // Set to 20 rows per page
    currentPage = 1; // Current page number
    bottomIconImg = orbitguardPng;
    sideMenuElementName = 'maneuver-detection-menu';
    sideMenuElementHtml = html `
    <div id="maneuver-detection-menu" class="side-menu-parent start-hidden text-select">
      <div id="maneuver-detection-content" class="side-menu">
        <div class="row">
          <h1 class="center-align">OrbitGuard</h1>
          <table id="maneuver-detection-table" class="center-align"></table>
          <sub class="center-align">*OrbitGuard Data provided by MSBAI.</sub>
          <div id="pagination-controls" class="pagination">
            <button id="prev-page" class="pagination-btn">Previous</button>
            <span id="current-page" class="pagination-text">Page 1</span>
            <button id="next-page" class="pagination-btn">Next</button>
          </div>
        </div>
      </div>
    </div>`;
    menuMode = [MenuMode.BASIC, MenuMode.ADVANCED, MenuMode.ALL];
    dragOptions = {
        isDraggable: true,
        minWidth: 1200,
        maxWidth: 1600,
    };
    bottomIconCallback = () => {
        if (this.isMenuButtonActive) {
            this.parseManeuverData_();
        }
    };
    addJs() {
        super.addJs();
        keepTrackApi.on(EventBusEvent.uiManagerFinal, this.uiManagerFinal_.bind(this));
        keepTrackApi.on(EventBusEvent.onCruncherMessage, () => {
            if (this.selectSatIdOnCruncher_ !== null) {
                keepTrackApi.getPlugin(SelectSatManager)?.selectSat(this.selectSatIdOnCruncher_);
                this.selectSatIdOnCruncher_ = null;
            }
        });
    }
    uiManagerFinal_() {
        getEl(this.sideMenuElementName).addEventListener('click', (evt) => {
            const el = evt.target?.parentElement;
            if (!el?.classList.contains('maneuver-object')) {
                return;
            }
            showLoading(() => {
                const hiddenRow = el.dataset?.row ?? null;
                if (hiddenRow !== null) {
                    this.eventClicked_(parseInt(hiddenRow));
                }
            });
        });
        // Add event listeners for pagination buttons
        getEl('prev-page').addEventListener('click', this.goToPreviousPage_.bind(this));
        getEl('next-page').addEventListener('click', this.goToNextPage_.bind(this));
    }
    eventClicked_(row) {
        const sat = keepTrackApi.getCatalogManager().sccNum2Sat(parseInt(this.orbitGuardEvents[row].satNo));
        if (!sat) {
            keepTrackApi.getUiManager().toast('Satellite appears to have decayed!', ToastMsgType.caution);
            return;
        }
        const now = new Date();
        const eventTime = new Date(this.orbitGuardEvents[row].event_end_timestamp);
        keepTrackApi.getTimeManager().changeStaticOffset(eventTime.getTime() - now.getTime());
        keepTrackApi.getMainCamera().state.isAutoPitchYawToTarget = false;
        keepTrackApi.getUiManager().doSearch(`${sat.sccNum5}`);
        this.selectSatIdOnCruncher_ = sat.id;
        this.closeSideMenu();
    }
    parseManeuverData_() {
        if (this.orbitGuardEvents.length === 0) {
            fetch(this.maneuverDataSrc, {
                method: 'GET',
                headers: {
                    'Authorization': this.bearerToken, // Add Bearer token from your script
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
                .then((maneuverList) => {
                this.setManeuverList_(maneuverList);
                this.createTable_();
                if (this.orbitGuardEvents.length === 0) {
                    errorManagerInstance.warn('No OrbitGuard data found!');
                }
            })
                .catch((error) => {
                errorManagerInstance.warn(`Error fetching OrbitGuard data: ${error.message}`);
            });
        }
    }
    setManeuverList_(maneuverList) {
        this.orbitGuardEvents = maneuverList;
        this.orbitGuardEvents.sort((a, b) => new Date(b.event_end_timestamp).getTime() - new Date(a.event_end_timestamp).getTime());
        this.orbitGuardEvents = this.orbitGuardEvents.filter((v, i, a) => a.findIndex((t) => t.satNo === v.satNo) === i);
        this.currentPage = 1; // Reset to the first page whenever new data is loaded
        this.updatePaginationControls_(); // Update pagination controls for the first page
    }
    updatePaginationControls_() {
        const totalPages = Math.ceil(this.orbitGuardEvents.length / this.pageSize);
        const pageText = getEl('current-page');
        pageText.innerText = `Page ${this.currentPage} of ${totalPages}`;
        const prevBtn = getEl('prev-page');
        const nextBtn = getEl('next-page');
        // Disable/Enable the previous/next buttons based on current page
        prevBtn.disabled = this.currentPage <= 1;
        nextBtn.disabled = this.currentPage >= totalPages;
    }
    goToNextPage_() {
        const totalPages = Math.ceil(this.orbitGuardEvents.length / this.pageSize);
        if (this.currentPage < totalPages) {
            this.currentPage++;
            this.createTable_(); // Rebuild the table for the new page
            this.updatePaginationControls_(); // Update pagination controls
        }
    }
    goToPreviousPage_() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.createTable_(); // Rebuild the table for the new page
            this.updatePaginationControls_(); // Update pagination controls
        }
    }
    createTable_() {
        try {
            const tbl = getEl('maneuver-detection-table');
            tbl.innerHTML = '';
            tbl.classList.add('centered');
            this.createHeaders_(tbl);
            this.createBody_(tbl);
        }
        catch {
            errorManagerInstance.warn('Error processing maneuver data!');
        }
    }
    createBody_(tbl) {
        // Calculate the starting and ending indices for the current page
        const startIdx = (this.currentPage - 1) * this.pageSize;
        const endIdx = startIdx + this.pageSize;
        const pageData = this.orbitGuardEvents.slice(startIdx, endIdx);
        // Create rows for the current page data
        pageData.forEach((_, i) => {
            this.createRow_(tbl, startIdx + i); // Adjust row index
        });
    }
    createHeaders_(tbl) {
        const tr = tbl.insertRow();
        const names = [
            'NORAD',
            'Event Start Time',
            'Event End Time',
            'Maneuver Class',
            'Maneuver Probability (%)',
            'Orbit Out of Family',
            'Stability Change',
            'Stability Change Probability (%)',
        ];
        for (const name of names) {
            const column = tr.insertCell();
            column.appendChild(document.createTextNode(name));
            column.setAttribute('style', 'text-decoration: underline');
            column.setAttribute('class', 'center');
        }
    }
    createRow_(tbl, i) {
        const tr = tbl.insertRow();
        tr.setAttribute('class', 'maneuver-object link');
        tr.setAttribute('data-row', i.toString());
        const formatDateTime = (isoString) => {
            const date = new Date(isoString.slice(-1).toUpperCase() === 'Z' ? isoString : `${isoString}Z`); // Ensure UTC parsing
            const iso = date.toISOString().replace('T', ' ').split('.')[0];
            return iso;
        };
        const cells = [
            this.orbitGuardEvents[i].satNo,
            formatDateTime(this.orbitGuardEvents[i].event_start_timestamp),
            formatDateTime(this.orbitGuardEvents[i].event_end_timestamp),
            this.orbitGuardEvents[i].maneuver_class ?? 'None',
            (this.orbitGuardEvents[i].maneuver_probability * 100).toFixed(2),
            this.orbitGuardEvents[i].oof_detection === 0 ? 'False' : 'True',
            this.orbitGuardEvents[i].stability_change_detection === 0 ? 'False' : 'True',
            (this.orbitGuardEvents[i].stability_change_probability * 100).toFixed(2),
        ];
        cells.forEach((cellText, index) => {
            const cell = OrbitGuardMenuPlugin.createCell_(tr, cellText);
            if (this.shouldHighlightRed_(index, i)) {
                cell.classList.add('highlight-red');
            }
        });
        return tr;
    }
    shouldHighlightRed_(index, rowIndex) {
        const event = this.orbitGuardEvents[rowIndex];
        // Highlight based on column index
        switch (index) {
            case 3: // "Maneuver Class" column (index 3)
                if (event.maneuver_class !== null) {
                    return true; // Highlight "maneuver_class" if it's not None
                }
                break;
            case 5: // "Orbit Out of Family" column (index 5)
                if (event.oof_detection !== 0) {
                    return true; // Highlight "Orbit Out of Family" if it's true
                }
                break;
            case 6: // "Stability Change Detection" column (index 6)
                if (event.stability_change_detection !== 0) {
                    return true; // Highlight "stability_change_detection" if it's not 0
                }
                break;
            case 7: // "Stability Change Probability" column (index 7)
                if (event.stability_change_probability !== 0.0) {
                    return true; // Highlight "stability_change_probability" if it's not 0.0
                }
                break;
            default:
                break;
        }
        return false;
    }
    static createCell_(tr, text) {
        const cell = tr.insertCell();
        cell.appendChild(document.createTextNode(text));
        return cell;
    }
}
